import { useState } from 'react'
import { Container, CircularProgress } from '@mui/material'
import Spacebox from '../components/Spacebox'
import Flexbox from '../components/Flexbox'
import { formatUSPhoneNumber } from '../utilities/utils'
import { useSelector } from 'react-redux'

// ─── helpers ──────────────────────────────────────────────────────────────────
function detectSearchType(input) {
    const trimmed = input.trim()
    if (trimmed.includes('@')) return 'email'
    if (/^[\d\s\-()+]+$/.test(trimmed) && trimmed.replace(/\D/g, '').length >= 10) return 'phone'
    return 'contact_id'
}

async function searchLead(input) {
    const type = detectSearchType(input)
    let url

    if (type === 'email')
        url = `/api/users/email/retell?email=${encodeURIComponent(input.trim())}`
    else if (type === 'phone')
        url = `/api/users/phone/retell?phone=${encodeURIComponent(input.trim())}`
    else
        url = `/api/users/${encodeURIComponent(input.trim())}`

    const res = await fetch(url, { credentials: 'include' })
    const json = await res.json()

    if (!json.success)
        throw new Error(json.msg || 'Lead not found')

    return json.data
}

// ─── sub-components ───────────────────────────────────────────────────────────
function LeadCard({ user, mode }) {
    const hasScp = !!user.unsecured_cc_debt
    const shade = mode.dark ? '#2a2a2a' : '#f0f2f5'
    const border = mode.dark ? '#3a3a3a' : '#e4e6eb'

    return (
        <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', background: shade, borderBottom: `1px solid ${border}` }}>
                <small style={{ fontWeight: 700, opacity: .6 }}>LEAD PREVIEW</small>
            </div>
            <div style={{ padding: '16px' }}>
                <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: '18px' }}>
                    {user.first_name} {user.last_name}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <InfoItem label="Phone" value={formatUSPhoneNumber(String(user.phone))} />
                    <InfoItem label="State" value={user.state || '—'} />
                    <InfoItem label="Debt Amount" value={user.debt_amount ? `$${Number(user.debt_amount).toLocaleString()}` : '—'} />
                    <InfoItem label="Buyer" value={user.buyer || 'Not assigned'} />
                    <InfoItem label="Contact ID" value={user.contact_id} mono />
                    {hasScp && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{
                                background: '#1877F215', color: '#1877F2', fontSize: '12px',
                                fontWeight: 600, padding: '3px 10px', borderRadius: 100,
                                border: '1px solid #1877F230'
                            }}>
                                SCP Lead — dedicated agent &amp; number will be used
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function InfoItem({ label, value, mono }) {
    return (
        <div>
            <div style={{ fontSize: '11px', opacity: .5, fontWeight: 600, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '14px', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
        </div>
    )
}

function AgentCard({ agent, selected, onSelect, mode }) {
    const border = selected
        ? (agent.isTest ? '#f59e0b' : '#22c55e')
        : (mode.dark ? '#3a3a3a' : '#e4e6eb')
    const bg = selected
        ? (agent.isTest ? '#f59e0b10' : '#22c55e10')
        : 'transparent'

    return (
        <div
            onClick={onSelect}
            style={{
                border: `2px solid ${border}`, borderRadius: 10, padding: '14px 16px',
                cursor: 'pointer', transition: 'all .2s', background: bg, flex: 1
            }}
        >
            <Flexbox alignItems="center" justifyContent="space-between">
                <span style={{ fontWeight: 700, fontSize: '15px' }}>{agent.label}</span>
                <span style={{
                    fontSize: '11px', fontWeight: 700, padding: '2px 8px',
                    borderRadius: 100,
                    background: agent.isTest ? '#f59e0b20' : '#22c55e20',
                    color: agent.isTest ? '#d97706' : '#16a34a',
                    border: `1px solid ${agent.isTest ? '#f59e0b40' : '#22c55e40'}`
                }}>
                    {agent.isTest ? 'TEST' : 'LIVE'}
                </span>
            </Flexbox>
            <Spacebox padding="4px" />
            <small style={{ opacity: .6, lineHeight: 1.5 }}>{agent.description}</small>
        </div>
    )
}

// ─── main page ────────────────────────────────────────────────────────────────
const AGENTS = [
    {
        key: 'live',
        label: 'Jessica — Outbound Agent',
        description: 'Live agent with full capabilities. Can transfer calls to buyers upon qualification.',
        endpoint: '/webhook/ghl/trigger-call',
        isTest: false
    },
    {
        key: 'test',
        label: 'Test Agent',
        description: 'Test environment only. Will call the number but cannot perform any call transfers.',
        endpoint: '/webhook/ghl/trigger-call/test',
        isTest: true
    }
]

const TriggerCallPage = ({ title }) => {
    document.querySelector('title').innerHTML = title

    const mode = useSelector(state => state.darkmode.value)

    const [query, setQuery] = useState('')
    const [searching, setSearching] = useState(false)
    const [searchError, setSearchError] = useState('')
    const [lead, setLead] = useState(null)

    const [selectedAgent, setSelectedAgent] = useState('live')

    const [triggering, setTriggering] = useState(false)
    const [triggerResult, setTriggerResult] = useState(null) // { success, msg }

    const shade = mode.dark ? '#2a2a2a' : '#f9fafb'
    const border = mode.dark ? '#3a3a3a' : '#e4e6eb'

    const handleSearch = async (e) => {
        e.preventDefault()
        if (!query.trim()) return
        setSearching(true)
        setSearchError('')
        setLead(null)
        setTriggerResult(null)
        try {
            const user = await searchLead(query)
            setLead(user)
        } catch (err) {
            setSearchError(err.message)
        } finally {
            setSearching(false)
        }
    }

    const handleTrigger = async () => {
        if (!lead) return
        setTriggering(true)
        setTriggerResult(null)
        try {
            const agent = AGENTS.find(a => a.key === selectedAgent)
            const url = `${agent.endpoint}?contact_id=${encodeURIComponent(lead.contact_id)}`
            const res = await fetch(url, { credentials: 'include' })
            const json = await res.json()
            setTriggerResult({ success: json.success, msg: json.msg || (json.success ? 'Call initiated successfully.' : 'Server returned an error.') })
        } catch (err) {
            setTriggerResult({ success: false, msg: err.message })
        } finally {
            setTriggering(false)
        }
    }

    const activeAgent = AGENTS.find(a => a.key === selectedAgent)

    return (
        <div style={{ minHeight: '100dvh', paddingBottom: 60 }}>
            <Container maxWidth="sm">
                <Spacebox padding="20px" />

                <p style={{ fontWeight: 700, fontSize: '20px', margin: 0 }}>Trigger Outbound Call</p>
                <small style={{ opacity: .5 }}>Look up a lead and initiate a Retell AI call.</small>

                <Spacebox padding="20px" />

                {/* ── search ── */}
                <form onSubmit={handleSearch}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            value={query}
                            onChange={e => { setQuery(e.target.value); setSearchError('') }}
                            placeholder="Contact ID, email, or phone number"
                            style={{
                                flex: 1, padding: '10px 14px', borderRadius: 8,
                                border: `1px solid ${searchError ? '#ef4444' : border}`,
                                background: shade, fontSize: '14px',
                                outline: 'none', color: 'inherit'
                            }}
                        />
                        <button
                            type="submit"
                            disabled={searching || !query.trim()}
                            style={{
                                padding: '10px 20px', borderRadius: 8, border: 'none',
                                background: '#0067CF', color: 'white', fontWeight: 600,
                                cursor: searching || !query.trim() ? 'not-allowed' : 'pointer',
                                opacity: !query.trim() ? .5 : 1, fontSize: '14px',
                                display: 'flex', alignItems: 'center', gap: 8
                            }}
                        >
                            {searching ? <CircularProgress size={14} style={{ color: 'white' }} /> : null}
                            {searching ? 'Searching…' : 'Search'}
                        </button>
                    </div>
                    {searchError && (
                        <small style={{ color: '#ef4444', marginTop: 6, display: 'block' }}>{searchError}</small>
                    )}
                    <small style={{ opacity: .45, marginTop: 6, display: 'block' }}>
                        Accepts contact ID, email address, or US phone number
                    </small>
                </form>

                {/* ── lead preview ── */}
                {lead && (
                    <>
                        <Spacebox padding="20px" />
                        <LeadCard user={lead} mode={mode} />
                    </>
                )}

                {/* ── agent selection ── */}
                {lead && (
                    <>
                        <Spacebox padding="20px" />
                        <div>
                            <small style={{ fontWeight: 700, opacity: .6 }}>SELECT AGENT</small>
                            <Spacebox padding="8px" />
                            <Flexbox alignItems="stretch" style={{ gap: 10 }}>
                                {AGENTS.map(agent => (
                                    <AgentCard
                                        key={agent.key}
                                        agent={agent}
                                        selected={selectedAgent === agent.key}
                                        onSelect={() => { setSelectedAgent(agent.key); setTriggerResult(null) }}
                                        mode={mode}
                                    />
                                ))}
                            </Flexbox>
                        </div>

                        {/* ── test agent warning ── */}
                        {activeAgent.isTest && (
                            <>
                                <Spacebox padding="12px" />
                                <div style={{
                                    background: '#fef3c7', border: '1px solid #f59e0b',
                                    borderRadius: 8, padding: '12px 16px',
                                    display: 'flex', gap: 10, alignItems: 'flex-start'
                                }}>
                                    <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 700, fontSize: '13px', color: '#92400e' }}>
                                            Test Agent Selected
                                        </p>
                                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#92400e', lineHeight: 1.5 }}>
                                            This agent is for testing purposes only. It will dial the lead's number but
                                            <strong> cannot perform any call transfers to buyers</strong>. Use the Live agent for production calls.
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}

                        <Spacebox padding="20px" />

                        {/* ── trigger button ── */}
                        <button
                            onClick={handleTrigger}
                            disabled={triggering}
                            style={{
                                width: '100%', padding: '13px', borderRadius: 8, border: 'none',
                                background: activeAgent.isTest ? '#f59e0b' : '#0067CF',
                                color: 'white', fontWeight: 700, fontSize: '15px',
                                cursor: triggering ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                transition: 'opacity .2s', opacity: triggering ? .7 : 1
                            }}
                        >
                            {triggering && <CircularProgress size={16} style={{ color: 'white' }} />}
                            {triggering
                                ? 'Initiating call…'
                                : `Trigger ${activeAgent.isTest ? 'Test' : 'Live'} Call`}
                        </button>

                        {/* ── result ── */}
                        {triggerResult && (
                            <>
                                <Spacebox padding="14px" />
                                <div style={{
                                    borderRadius: 8, padding: '12px 16px',
                                    background: triggerResult.success ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${triggerResult.success ? '#86efac' : '#fca5a5'}`,
                                    display: 'flex', gap: 10, alignItems: 'center'
                                }}>
                                    <span style={{ fontSize: '20px' }}>{triggerResult.success ? '✅' : '❌'}</span>
                                    <span style={{
                                        fontSize: '14px', fontWeight: 600,
                                        color: triggerResult.success ? '#15803d' : '#dc2626'
                                    }}>
                                        {triggerResult.msg}
                                    </span>
                                </div>
                            </>
                        )}
                    </>
                )}
            </Container>
        </div>
    )
}

export default TriggerCallPage
